import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import reject from 'lodash/reject';
import transform from 'lodash/transform';
import isNil from 'lodash/isNil';
import toString from 'lodash/toString';
import toSafeInteger from 'lodash/toSafeInteger';
import type { CallbackDataParams } from 'echarts/types/dist/shared';
import type EChartsReactCore from 'echarts-for-react/lib/core';
import { useIntl } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/system/colorManipulator';
import Box from '@mui/material/Box';
// Skillmore UI Components
import { getTruncatedTitle } from '@empathco/ui-components/src/helpers/strings';
import { type EChartsMouseEvent, setEChartInstanceOption } from '@empathco/ui-components/src/helpers/echarts';
import { spacing } from '@empathco/ui-components/src/helpers/styles';
import useCombinedRefs from '@empathco/ui-components/src/hooks/useCombinedRefs';
// local imports
import { DAOrgUsage } from '../graphql/types';
import { GlobalEChartsStyles } from '../config/params';
import { svgPath as Hexagon } from '../icons/Hexagon';
import Chart from '../elements/Chart';
// SCSS imports
import { radarSeries1, radarSeries4 } from '../styles/modules/Chart.module.scss';
import {
  chart, chartPreview, tooltipHeader, marker, tooltipValue, tooltipRow
} from './TopChartRadar.module.scss';

const overflowXSx: SxProps<Theme> = { overflowX: 'auto' } as const;

type TopChartRadarProps = {
  readonly preview?: boolean;
  readonly data?: DAOrgUsage[];
  readonly path?: string;
  readonly pending?: boolean | null;
}

const TopChartRadar = forwardRef<EChartsReactCore, TopChartRadarProps>(({
  preview = false,
  data: parentData,
  path,
  pending = false
  // eslint-disable-next-line max-lines-per-function
}, ref) => {
  const theme = useTheme();
  const { formatMessage, formatNumber } = useIntl();

  const innerRef = useRef<EChartsReactCore>(null);
  const chartRef = useCombinedRefs<EChartsReactCore>(ref, innerRef);

  const [activeId, setActiveId] = useState<number>();

  const data = useMemo(() => reject(parentData, ({ employee_count, usage_count }) =>
    (employee_count || 0) < 1 && (usage_count || 0) < 1), [parentData]);

  const lastIndex = preview ? 6 : size(data);

  useEffect(() => {
    setActiveId(undefined);
  }, [data]);

  const colors = useMemo(
    () => [theme.palette.chart.radarSeries4, theme.palette.chart.radarSeries1],
    [theme]
  );

  const seriesNames = useMemo(() => [
    formatMessage({ id: 'hr.dashboard.radar.total' }),
    formatMessage({ id: 'hr.dashboard.radar.active' })
  ], [formatMessage]);

  const counts = useMemo(() => {
    const cnts = preview
      ? [
        [[350, 0], [375, 1], [350, 2], [400, 3], [350, 4], [400, 5]],
        [[300, 0], [100, 1], [270, 2], [240, 3], [280, 4], [220, 5]]
      ]
      : transform(data, (result, { usage_count, employee_count }, index) => {
          result[0].push([toSafeInteger(employee_count), index]);
          result[1].push([toSafeInteger(usage_count), index]);
        }, [[], []] as number[][][]);
    if (lastIndex === 1) {
      cnts[0].push([cnts[0][0][0] / 2, 1]);
      cnts[0].push([0, 2]);
      cnts[0].push([cnts[0][0][0] / 2, 3]);
      cnts[0].push([cnts[0][0][0], 4]);
      cnts[1].push([cnts[1][0][0] / 2, 1]);
      cnts[1].push([0, 2]);
      cnts[1].push([cnts[1][0][0] / 2, 3]);
      cnts[1].push([cnts[1][0][0], 4]);
    } else if (lastIndex === 2) {
      cnts[0] = [
        cnts[0][0],
        [(cnts[0][0][0] + cnts[0][1][0]) / 4, 1],
        [cnts[0][1][0], 2],
        [(cnts[0][0][0] + cnts[0][1][0]) / 4, 3],
        [cnts[0][0][0], 4]
      ];
      cnts[1] = [
        cnts[1][0],
        [(cnts[1][0][0] + cnts[1][1][0]) / 4, 1],
        [cnts[1][1][0], 2],
        [(cnts[1][0][0] + cnts[1][1][0]) / 4, 3],
        [cnts[1][0][0], 4]
      ];
    } else if (lastIndex >= 1) {
      cnts[0].push([cnts[0][0][0], lastIndex]);
      cnts[1].push([cnts[1][0][0], lastIndex]);
    }
    return cnts;
  }, [data, lastIndex, preview]);

  type Indicator = { id: number; title: string; abbr: string; };
  const indicators: Indicator[] = useMemo(() => {
    if (preview) return [{}, {}, {}, {}, {}, {}] as Indicator[];
    let result = map(data, (rec) => ({
      id: rec.id,
      abbr: toString(rec.id),
      title: getTruncatedTitle(rec.title)
    }));
    if (result.length === 1) result = [result[0], {} as Indicator, {} as Indicator, {} as Indicator];
    else if (result.length === 2) result = [result[0], {} as Indicator, result[1], {} as Indicator];
    return result;
  }, [data, preview]);

  const onEvents = useMemo(() => !preview && path ? {
    mouseover: ({ componentType, value }: EChartsMouseEvent) => {
      if (componentType === 'angleAxis') try {
        setActiveId(indicators[toSafeInteger(value)]?.id);
      } catch (_err) {
        setActiveId(undefined);
      }
    },
    mouseout: ({ componentType }: EChartsMouseEvent) => {
      if (componentType === 'angleAxis') setActiveId(undefined);
    }
  } : undefined, [indicators, path, preview]);

  const axisFormatter = useCallback((value: number) => {
    const { id, title } = indicators[value] || {};
    return title ? `{${activeId && id === activeId ? 'active' : 'normal'}Style|${title}}` : undefined;
  }, [activeId, indicators]);

  const tooltipFormatter = useCallback((params: CallbackDataParams[]) => {
    const { dataIndex } = params?.[0] || {};
    // eslint-disable-next-line no-nested-ternary
    const effectiveIdx = lastIndex === 1 ? [0, null, null, null, 0][dataIndex]
      : lastIndex === 2 ? [0, null, 1, null, 0][dataIndex]
      : (dataIndex < lastIndex && dataIndex) || 0;
    if (isNil(effectiveIdx)) return undefined;
    const { title, usage_count, employee_count } = (data?.[effectiveIdx || 0] || {}) as DAOrgUsage;
    return `<div><div class="${tooltipHeader}"><span>${title}</span></div><div class="${tooltipRow}"><span class="${
        marker} ${radarSeries4}">\u00A0</span><span>${
          formatMessage({ id: 'hr.dashboard.radar.total' })
        }</span><span class="${tooltipValue}">${
          formatNumber(employee_count || 0)
      }</span></div><div class="${tooltipRow}"><span class="${
        marker} ${radarSeries1}">\u00A0</span><span>${
          formatMessage({ id: 'hr.dashboard.radar.active' })
        }</span><span class="${tooltipValue}">${
          formatNumber(usage_count || 0)
      }</span></div></div>`;
  }, [data, lastIndex, formatMessage, formatNumber]);

  useEffect(() => {
    if (!innerRef.current) return;

    const echartInstance = innerRef.current.getEchartsInstance();

    setEChartInstanceOption(echartInstance, {
      ...GlobalEChartsStyles,
      silent: Boolean(preview || pending),
      polar: preview ? {
        center: ['50%', '50%'],
        radius: '100%'
      } : {
        center: ['50%', '45%'],
        radius: '60%'
      },
      legend: preview ? null : {
        bottom: 0,
        padding: [0, 0, spacing(1.5), 0],
        icon: Hexagon,
        itemWidth: spacing(2.5),
        itemHeight: spacing(2.5),
        itemGap: spacing(6),
        itemStyle: {
          borderWidth: theme.shape.thinBorderWidth
        },
        textStyle: {
          fontSize: 14,
          fontStyle: 'italic',
          color: theme.palette.info.caption,
          padding: spacing(0.125)
        }
      },
      ...preview || pending ? {} : {
        tooltip: {
          show: true,
          confine: true,
          trigger: 'axis',
          axisPointer: { type: 'none' },
          formatter: tooltipFormatter,
          borderColor: theme.palette.misc.selectedBorder,
          borderWidth: theme.shape.borderWidth,
          backgroundColor: theme.palette.background.tooltipLight,
          extraCssText: `box-shadow: ${theme.shadows[5]}`
        }
      },
      angleAxis: {
        type: 'value',
        triggerEvent: Boolean(!preview && path),
        startAngle: (indicators.length > 2 && (
          (indicators.length % 5 === 0 && -18) || (indicators.length % 3 === 0 && -30)
        )) || -45,
        min: 0,
        max: indicators.length || 1,
        interval: 1,
        minInterval: 1,
        maxInterval: 1,
        splitNumber: indicators.length || 1,
        data: indicators,
        axisLine: {
          lineStyle: {
            color: theme.palette.chart.gridRadar,
            width: theme.shape.thinBorderWidth
          }
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.chart.gridRadar,
            width: theme.shape.thinBorderWidth
          }
        },
        axisTick: { show: false },
        axisLabel: preview ? null : {
          formatter: axisFormatter,
          rich: {
            normalStyle: {
              color: theme.palette.secondary.text,
              fontSize: 16,
              lineHeight: 16,
              fontWeight: theme.typography.fontWeightMedium,
              padding: [spacing(0.375), spacing(1.5), spacing(0.375), spacing(1.5)]
            },
            activeStyle: {
              color: theme.palette.secondary.text,
              fontSize: 16,
              lineHeight: 16,
              fontWeight: theme.typography.fontWeightMedium,
              borderRadius: theme.shape.borderRadius,
              padding: [spacing(0.375), spacing(1.5), spacing(0.375), spacing(1.5)],
              backgroundColor: alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity)
            }
          }
        }
      },
      radiusAxis: {
        ...preview || lastIndex === 1 || lastIndex === 2 ? {} : {
          type: 'log',
          logBase: 10
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.chart.gridRadar,
            width: theme.shape.thinBorderWidth
          }
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.chart.gridRadar,
            width: theme.shape.thinBorderWidth
          }
        },
        axisTick: { show: false },
        alignTicks: false,
        splitArea: { show: false },
        axisLabel: preview ? null : {
          show: true,
          showMinLabel: false,
          fontSize: 14,
          fontWeight: theme.typography.fontWeightRegular,
          color: theme.palette.text.label
        }
      },
      series: seriesNames.map((name, index) => ({
        coordinateSystem: 'polar',
        polarIndex: 0,
        type: 'line',
        symbol: 'none',
        ...lastIndex === 1 || lastIndex === 2 ? { smooth: true } : {},
        name,
        itemStyle: { color: colors[index] },
        lineStyle: {
          opacity: 0
        },
        areaStyle: {
          color: colors[index],
          opacity: 1 - theme.palette.action.disabledOpacity
        },
        data: counts[index]
      }))
    });
  }, [
    indicators, counts, lastIndex, pending, colors, seriesNames,
    axisFormatter, tooltipFormatter, theme, preview, path
  ]);

  const chartEl = (
    <Chart
        ref={chartRef}
        option={GlobalEChartsStyles}
        className={preview ? chartPreview : chart}
        onEvents={pending ? undefined : onEvents}
    />
  );

  return preview ? chartEl : (
    <Box sx={overflowXSx}>
      {chartEl}
    </Box>
  );
});

TopChartRadar.displayName = 'TopChartRadar';

export default memo(TopChartRadar);
